import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, Users, UsersRound, ShieldCheck, Lock } from 'lucide-react';

interface Department {
  id: string;
  name: string;
  slug: string | null;
  description: string | null;
  is_system: boolean;
  default_role: string | null;
  created_at: string;
  team_count?: number;
  member_count?: number;
  open_tasks?: number;
}

export function DepartmentManagement() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDept, setEditingDept] = useState<Department | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const { data: departments = [], isLoading } = useQuery({
    queryKey: ['departments-management'],
    queryFn: async () => {
      const { data: depts, error } = await supabase
        .from('internal_departments')
        .select('*')
        .order('is_system', { ascending: false })
        .order('name');
      if (error) throw error;

      // Get team counts, member counts, and open task counts
      const { data: teams } = await supabase
        .from('internal_teams')
        .select('id, department_id');
      const { data: members } = await supabase
        .from('internal_team_members')
        .select('id, team_id');
      const { data: tasks } = await supabase
        .from('internal_tasks')
        .select('id, team_id, status')
        .neq('status', 'done');

      return (depts || []).map((d: any) => {
        const deptTeams = (teams || []).filter((t: any) => t.department_id === d.id);
        const deptTeamIds = deptTeams.map((t: any) => t.id);
        const memberCount = (members || []).filter((m: any) => deptTeamIds.includes(m.team_id)).length;
        const openTasks = (tasks || []).filter((t: any) => deptTeamIds.includes(t.team_id)).length;
        return { ...d, team_count: deptTeams.length, member_count: memberCount, open_tasks: openTasks };
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: async ({ name, description }: { name: string; description: string }) => {
      const { error } = await supabase.from('internal_departments').insert({ name, description: description || null });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments-management'] });
      toast.success('Department created');
      closeDialog();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, name, description }: { id: string; name: string; description: string }) => {
      const { error } = await supabase.from('internal_departments').update({ name, description: description || null }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments-management'] });
      toast.success('Department updated');
      closeDialog();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('internal_departments').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments-management'] });
      toast.success('Department deleted');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const openCreate = () => {
    setEditingDept(null);
    setName('');
    setDescription('');
    setDialogOpen(true);
  };

  const openEdit = (dept: Department) => {
    setEditingDept(dept);
    setName(dept.name);
    setDescription(dept.description || '');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingDept(null);
    setName('');
    setDescription('');
  };

  const handleSubmit = () => {
    if (!name.trim()) return toast.error('Name is required');
    if (editingDept) {
      updateMutation.mutate({ id: editingDept.id, name: name.trim(), description: description.trim() });
    } else {
      createMutation.mutate({ name: name.trim(), description: description.trim() });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Department Management</h1>
          <p className="text-muted-foreground">Manage internal departments for your organization</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" /> Add Department
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{departments.length}</p>
              <p className="text-sm text-muted-foreground">Departments</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="h-12 w-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Users className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{departments.reduce((s, d) => s + (d.team_count || 0), 0)}</p>
              <p className="text-sm text-muted-foreground">Total Teams</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="h-12 w-12 rounded-xl bg-green-500/10 flex items-center justify-center">
              <UsersRound className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{departments.reduce((s, d) => s + (d.member_count || 0), 0)}</p>
              <p className="text-sm text-muted-foreground">Total Members</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Departments</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center py-8 text-muted-foreground">Loading...</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Default Role</TableHead>
                  <TableHead>Teams</TableHead>
                  <TableHead>Members</TableHead>
                  <TableHead title="Open tasks per member">Avg Load</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {departments.map((dept) => {
                  const avgLoad = dept.member_count ? ((dept.open_tasks || 0) / dept.member_count).toFixed(1) : '—';
                  return (
                    <TableRow key={dept.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {dept.is_system && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                          <div>
                            <div>{dept.name}</div>
                            {dept.description && (
                              <div className="text-xs text-muted-foreground font-normal max-w-[260px] truncate">{dept.description}</div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {dept.default_role ? (
                          <Badge variant="secondary" className="text-[10px] gap-1">
                            <ShieldCheck className="h-3 w-3" /> {dept.default_role}
                          </Badge>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{dept.team_count || 0}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{dept.member_count || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{avgLoad}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(dept)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          className="text-destructive hover:text-destructive disabled:opacity-30"
                          disabled={dept.is_system}
                          title={dept.is_system ? 'System departments cannot be deleted' : 'Delete'}
                          onClick={() => {
                            if (dept.is_system) return;
                            if (confirm(`Delete "${dept.name}" department?`)) {
                              deleteMutation.mutate(dept.id);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {departments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No departments yet. Create your first one!
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingDept ? 'Edit Department' : 'Add Department'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sales" />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
              {editingDept ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
